import {
  NewSelectedWebbedRoomDetails,
  WebbedHotel,
  WebbedRateBasis,
  WebbedSpecialRoomRequest,
} from "@/types/webbeds";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

interface PassengerDetails {
  firstName: string;
  lastName: string;
  gender: string;
  type: string;
}

interface Occupancy {
  roomTypeId: string;
  adults: number;
  children: number;
  passengersDetails: PassengerDetails[];
}

interface Stay {
  checkIn: string;
  checkOut: string;
}

type NewWebbedRoomDetailsWithoutRateBases = Omit<
  NewSelectedWebbedRoomDetails,
  "rateBases"
> & {
  rateBases?: WebbedRateBasis[];
};

interface WebbedStore {
  stay: Stay | null;
  hotelCode: string;
  roomsSpecials: { [key: string]: string[] } | null;
  occupancies: Occupancy[];
  orderId: string | null;
  selectedRooms: NewSelectedWebbedRoomDetails[];
  isSearchingHotels: boolean;
  setIsSearchingHotels: (isSearchingHotels: boolean) => void;
  hotelData: WebbedHotel | null;
  setStay: (stay: Stay | null) => void;
  toggleRoomSelectedSpecials: (roomIndex: number, special: string) => void;
  addRoomsSpecials: (roomId: string, specials: string[]) => void;
  setHotelCode: (stay: string) => void;
  setOccupancies: (occupancies: Occupancy[]) => void;
  setOrderId: (orderId: string) => void;
  setHotelData: (HotelData: WebbedHotel) => void;
  addOccupancy: (newOccupancy: Occupancy) => void;
  addPassenger: (roomIndex: number, amount?: number) => void;
  deletePassenger: (roomIndex: number, passengerIndex: number) => void;
  removeOccupancyById: (roomTypeId: string) => void;
  toggleOccupancy: (occupancy: Occupancy) => void;
  mapRoomIds: (rooms: { roomId: string }[]) => void;
  setOrderStatus: (
    roomIndex: number,
    status: "unsuccessful" | "successful",
  ) => void;
  toggleSelectedRoom: (
    room: NewWebbedRoomDetailsWithoutRateBases,
    allocationDetails: string,
    roomIndex?: number,
  ) => void;
  resetSelectedRoom: () => void;
  setFirstName: (
    roomTypeId: string,
    firstName: string,
    passengerIndex: number,
    roomIndex: number,
  ) => void;
  setLastName: (
    roomTypeId: string,
    lastName: string,
    passengerIndex: number,
    roomIndex: number,
  ) => void;
  setGender: (
    roomTypeId: string,
    gender: string,
    passengerIndex: number,
    roomIndex: number,
  ) => void;
  setType: (
    roomTypeId: string,
    type: string,
    passengerIndex: number,
    roomIndex: number,
  ) => void;
}

const useWebbedStore = create<WebbedStore>()(
  persist(
    (set) => ({
      stay: null,
      hotelCode: "",
      hotelData: null,
      occupancies: [],
      selectedRooms: [],
      orderId: null,
      isSearchingHotels: false,
      roomsSpecials: null,
      addRoomsSpecials: (roomId: string, specials: string[]) =>
        set((state) => ({
          roomsSpecials: {
            ...state.roomsSpecials,
            [roomId]: specials,
          },
        })),
      setIsSearchingHotels: (isSearchingHotels: boolean) =>
        set({ isSearchingHotels }),

      addPassenger: (roomIdnex, amount = 0) =>
        set((state) => {
          return {
            selectedRooms: state.selectedRooms.map((room, index) => {
              if (index === roomIdnex && room.occupancies) {
                const updatedPassengers = [
                  ...room.occupancies.passengersDetails,
                ];

                if (amount) {
                  Array.from({ length: amount - updatedPassengers.length }).map(
                    (_, index) => {
                      updatedPassengers.push({
                        firstName: "",
                        lastName: "",
                        gender: "",
                        type: "",
                      });
                    },
                  );
                } else {
                  updatedPassengers.push({
                    firstName: "",
                    lastName: "",
                    gender: "",
                    type: "",
                  });
                }

                // Update the firstName at the specified index

                return {
                  ...room,
                  occupancies: {
                    ...room.occupancies,
                    passengersDetails: updatedPassengers,
                  },
                };
              } else {
                return room;
              }
            }),
          };
        }),
      deletePassenger: (roomIdnex, passengerIndex) =>
        set((state) => {
          return {
            selectedRooms: state.selectedRooms.map((room, index) => {
              if (index === roomIdnex && room.occupancies) {
                const updatedPassengers = [
                  ...room.occupancies.passengersDetails,
                ];

                updatedPassengers.splice(passengerIndex, 1);
                // Update the firstName at the specified index
                return {
                  ...room,
                  occupancies: {
                    ...room.occupancies,
                    passengersDetails: updatedPassengers,
                  },
                };
              } else {
                return room;
              }
            }),
          };
        }),
      setOrderId: (orderId) => set(() => ({ orderId })),
      mapRoomIds: (rooms) =>
        set((state) => {
          const updatedRooms = state.selectedRooms.map((room, index) => {
            const roomId = rooms[index]?.roomId;
            return roomId ? { ...room, roomId } : room;
          });

          return { selectedRooms: updatedRooms };
        }),
      toggleRoomSelectedSpecials: (roomIndex, special) =>
        set((state) => {
          const updatedRooms = state.selectedRooms.map((room) => {
            if (room.roomIndex === roomIndex) {
              const selectedSpecials = room.selectedSpecials ?? [];
              const newSpecials = selectedSpecials.includes(special)
                ? selectedSpecials.filter((s) => s !== special) // Remove special if already exists
                : [...selectedSpecials, special]; // Add special if not exists

              return { ...room, selectedSpecials: newSpecials };
            }
            return room;
          });
          return { selectedRooms: updatedRooms };
        }),
      setOrderStatus: (roomIndex, status) =>
        set((state) => {
          const updatedRooms = state.selectedRooms.map((room) =>
            room.roomIndex === roomIndex
              ? { ...room, orderStatus: status }
              : room,
          );
          return { selectedRooms: updatedRooms };
        }),
      setHotelData: (hotelData: WebbedHotel) => set({ hotelData }),
      setHotelCode: (hotelCode: string) => set({ hotelCode }),
      resetSelectedRoom: () => set({ selectedRooms: [], roomsSpecials: null }),
      setStay: (stay) => set({ stay }),
      setOccupancies: (occupancies) => set({ occupancies }),
      addOccupancy: (newOccupancy) =>
        set((state) => ({
          occupancies: [...state.occupancies, newOccupancy],
        })),
      removeOccupancyById: (roomTypeId) =>
        set((state) => ({
          occupancies: state.occupancies.filter(
            (occupancy) => occupancy.roomTypeId !== roomTypeId,
          ),
        })),
      toggleOccupancy: (occupancy) =>
        set((state) => {
          const exists = state.occupancies.some(
            (o) => o.roomTypeId === occupancy.roomTypeId,
          );
          return {
            occupancies: exists
              ? state.occupancies.filter(
                  (o) => o.roomTypeId !== occupancy.roomTypeId,
                )
              : [...state.occupancies, occupancy],
          };
        }),
      toggleSelectedRoom: (room, allocationDetails: string, roomIndex) =>
        set((state) => {
          const selectedRoomsHolder = [...state.selectedRooms];
          const index = selectedRoomsHolder.findIndex(
            (item) =>
              item.roomTypeId === room.roomTypeId &&
              item.roomIndex === roomIndex,
          );
          if (index !== -1) {
            selectedRoomsHolder.splice(index, 1);
          } else {
            if (
              selectedRoomsHolder.filter(
                (selected) => selected.roomTypeId === room.roomTypeId,
              )?.length > 0 &&
              selectedRoomsHolder.length >=
                Number(room.rateBases?.[0].leftToSell)
            ) {
              return { selectedRooms: selectedRoomsHolder };
            }
            const occupancyHolder: Occupancy = {
              roomTypeId: room.roomTypeId,
              adults: 1,
              children: 0,
              passengersDetails: [
                {
                  firstName: "",
                  lastName: "",
                  gender: "",
                  type: "",
                },
              ],
            };
            const selectedRate = room?.rateBases?.find(
              (rate) => rate.allocationDetails === allocationDetails,
            );
            if (selectedRate) {
              selectedRoomsHolder.push({
                ...room,
                roomIndex: roomIndex === undefined ? Infinity : roomIndex,
                occupancies: occupancyHolder,
                selectedRate,
              });
            }
          }
          return {
            selectedRooms: selectedRoomsHolder.sort((a, b) => {
              const indexA = a.roomIndex ?? Infinity;
              const indexB = b.roomIndex ?? Infinity;
              return indexA - indexB;
            }),
          };
        }),
      setFirstName: (roomTypeId, firstName, passengerIndex, roomIndex) =>
        set((state) => {
          return {
            selectedRooms: state.selectedRooms.map((room) => {
              if (
                room.roomTypeId === roomTypeId &&
                room.occupancies &&
                roomIndex === room.roomIndex
              ) {
                const updatedPassengers = [
                  ...room.occupancies.passengersDetails,
                ];

                // Update the firstName at the specified index
                updatedPassengers[passengerIndex].firstName = firstName;
                return {
                  ...room,
                  occupancies: {
                    ...room.occupancies,
                    passengersDetails: updatedPassengers,
                  },
                };
              } else {
                return room;
              }
            }),
          };
        }),
      setLastName: (roomTypeId, lastName, passengerIndex, roomIndex) =>
        set((state) => {
          return {
            selectedRooms: state.selectedRooms.map((room) => {
              if (
                room.roomTypeId === roomTypeId &&
                room.occupancies &&
                roomIndex === room.roomIndex
              ) {
                const updatedPassengers = [
                  ...room.occupancies.passengersDetails,
                ];

                // Update the firstName at the specified index
                updatedPassengers[passengerIndex].lastName = lastName;
                return {
                  ...room,
                  occupancies: {
                    ...room.occupancies,
                    passengersDetails: updatedPassengers,
                  },
                };
              } else {
                return room;
              }
            }),
          };
        }),

      setGender: (roomTypeId, gender, passengerIndex, roomIndex) =>
        set((state) => {
          return {
            selectedRooms: state.selectedRooms.map((room) => {
              if (
                room.roomTypeId === roomTypeId &&
                room.occupancies &&
                roomIndex === room.roomIndex
              ) {
                const updatedPassengers = [
                  ...room.occupancies.passengersDetails,
                ];

                // Update the firstName at the specified index
                updatedPassengers[passengerIndex].gender = gender;
                return {
                  ...room,
                  occupancies: {
                    ...room.occupancies,
                    passengersDetails: updatedPassengers,
                  },
                };
              } else {
                return room;
              }
            }),
          };
        }),
      setType: (roomTypeId, type, passengerIndex, roomIndex) =>
        set((state) => {
          return {
            selectedRooms: state.selectedRooms.map((room) => {
              if (
                room.roomTypeId === roomTypeId &&
                room.occupancies &&
                roomIndex === room.roomIndex
              ) {
                const updatedPassengers = [
                  ...room.occupancies.passengersDetails,
                ];

                // Update the firstName at the specified index
                updatedPassengers[passengerIndex].type = type;
                return {
                  ...room,
                  occupancies: {
                    ...room.occupancies,
                    passengersDetails: updatedPassengers,
                  },
                };
              } else {
                return room;
              }
            }),
          };
        }),
    }),
    {
      name: "webbed-store",
      partialize: (state) => ({
        selectedRooms: state.selectedRooms,
        roomsSpecials: state.roomsSpecials,
      }),
      storage: createJSONStorage(() => localStorage),
      version: 2,
    },
  ),
);

export default useWebbedStore;
